import { useEffect, useState } from 'react'
import { api, ApiError } from './api'
import { navigate } from './App'
import { ImportDialog } from './ImportDialog'
import { Logo, Modal, Spinner, toast } from './ui'
import { dur } from './util'

type Row = {
  id: string; slug: string; title: string; client: string; shoot_date: string; updated_at: number
  take_count: number; ready_count: number; sync_seconds: number; select_count: number; open_jobs: number; editing: number
}

export function Home({ me, onSignOut }: { me: { email: string; name: string }; onSignOut: () => void }) {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [importFor, setImportFor] = useState<string | null>(null)

  const load = () => api.get<{ projects: Row[] }>('projects').then((r) => setRows(r.projects)).catch((e) => toast(e.message, 'err'))
  useEffect(() => {
    load()
    const t = setInterval(load, 15000)
    return () => clearInterval(t)
  }, [])

  return (
    <div className="home">
      <header className="topbar">
        <div className="brand"><Logo /><span>Selects</span></div>
        <div className="grow" />
        <span className="muted small">{me.email}</span>
        <button className="btn btn-ghost" onClick={onSignOut}>Sign out</button>
      </header>
      <main className="home-main">
        <div className="home-head">
          <div>
            <h1>Projects</h1>
            <p className="muted">Shoots with interview footage on Osmo Cloud. Transcribe, share with the client, pull the story.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>New project</button>
        </div>
        {!rows ? (
          <div className="center-pad"><Spinner /></div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <h3>No projects yet</h3>
            <p>Start one, upload the footage (or pick it on Osmo Cloud), and we line up the cameras and build the transcripts.</p>
            <button className="btn btn-primary" onClick={() => setCreating(true)}>New project</button>
          </div>
        ) : (
          <div className="project-grid">
            {rows.map((r) => (
              <a key={r.id} className="project-card" href={`/p/${r.id}`} onClick={(e) => { e.preventDefault(); navigate(`/p/${r.id}`) }}>
                <div className="pc-client">{r.client || 'Osmo'}</div>
                <div className="pc-title">{r.title}</div>
                <div className="pc-meta">
                  {r.shoot_date && <span>{r.shoot_date}</span>}
                  <span>{r.take_count} takes</span>
                  {r.sync_seconds > 0 && <span>{dur(r.sync_seconds)} of interview</span>}
                  <span>{r.select_count} selects</span>
                </div>
                {r.open_jobs > 0 ? (
                  <div className="pc-status working"><Spinner small /> {r.take_count ? (r.ready_count < r.take_count ? `Transcribing ${r.ready_count}/${r.take_count}` : 'Processing footage') : 'Reading the footage'}</div>
                ) : r.editing > 0 ? (
                  <div className="pc-status working"><Spinner small /> The Osmo agent is cutting</div>
                ) : r.take_count === 0 ? (
                  <div className="pc-status">No media yet</div>
                ) : (
                  <div className="pc-status ready">Ready</div>
                )}
              </a>
            ))}
          </div>
        )}
      </main>
      {creating && (
        <NewProject
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            setImportFor(id)
            load()
          }}
        />
      )}
      {importFor && (
        <ImportDialog
          projectId={importFor}
          onClose={() => {
            const id = importFor
            setImportFor(null)
            navigate(`/p/${id}`)
          }}
        />
      )}
    </div>
  )
}

function NewProject({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [title, setTitle] = useState('')
  const [client, setClient] = useState('')
  const [date, setDate] = useState('')
  const [vocab, setVocab] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr('')
    try {
      const r = await api.post<{ id: string }>('projects', { title, client, shoot_date: date, vocabulary: vocab })
      onCreated(r.id)
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not create it.')
      setBusy(false)
    }
  }
  return (
    <Modal title="New project" onClose={onClose}>
      <form onSubmit={submit} className="form">
        <label className="field"><span>Project</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Eve Legal: Mike's story" required autoFocus maxLength={120} /></label>
        <div className="row2">
          <label className="field"><span>Client</span><input value={client} onChange={(e) => setClient(e.target.value)} placeholder="Eve Legal" maxLength={120} /></label>
          <label className="field"><span>Shoot date</span><input value={date} onChange={(e) => setDate(e.target.value)} placeholder="Oct 9, 2026" maxLength={40} /></label>
        </div>
        <label className="field">
          <span>Names and terms</span>
          <textarea value={vocab} onChange={(e) => setVocab(e.target.value)} rows={3} placeholder="People, companies and jargon in the interviews, so the transcript spells them right. e.g. Casey, Michael Dickerson, a16z" maxLength={2000} />
        </label>
        {err && <p className="error">{err}</p>}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create and add footage'}</button>
        </div>
      </form>
    </Modal>
  )
}
